import React, { useEffect, useState, useRef } from 'react'
import { useSessionCleanup, useSessionStateStore } from '@renderer/stores/sessionState'
import { useCountdown } from '@renderer/utils/countdownUtils'
import QRCode from 'qrcode'
import { useConfigStore } from '@renderer/stores/configState'
import { Button } from '@renderer/components'
import { FaQrcode, FaImage } from 'react-icons/fa'

import ConsentModal from '@renderer/components/ConsentModal'
import { useNavigate, useParams } from 'react-router-dom'
import { resolveGallerySessionUrl } from '@renderer/utils/galleryUrlUtils'

const EditResultPage: React.FC = () => {
  const { gallery, id, setSessionState, uploadSessionState, logActivity } = useSessionStateStore()
  const sessionCleanup = useSessionCleanup()
  const { config: globalConfig } = useConfigStore()
  const config = globalConfig?.pages.result
  const [stripPreview, setStripPreview] = useState('')
  const [qrCodeImage, setQrCodeImage] = useState('')
  const [remainingTime, setRemainingTime] = useState<string>('--:--')
  const [showConsent, setShowConsent] = useState(true)
  const [consentGiven, setConsentGiven] = useState<boolean | null>(null)
  const isPotrait =
    (globalConfig?.screenSize.height || 1080) > (globalConfig?.screenSize.width || 1920)
  const hasCompleted = useRef(false)
  const navigate = useNavigate()
  // Initialize countdown hook
  const resultCountdown = useCountdown(config?.settings.resultTimeout || 120)
  const { projectId } = useParams() || ''

  // Handle consent response
  const handleConsent = async (consent: boolean): Promise<void> => {
    setConsentGiven(consent)
    setShowConsent(false)
    resultCountdown.start()

    // Update session state with consent
    setSessionState({
      userConsent: {
        photoPublication: consent,
        consentTimestamp: new Date()
      }
    })

    // Log consent activity for editing
    logActivity(
      `User ${consent ? 'granted' : 'declined'} photo publication consent for edited strip`,
      {
        consent,
        timestamp: new Date(),
        mode: 'edit'
      }
    )

    // CRITICAL: Wait for state to propagate, then upload to ensure consent is saved
    await new Promise((resolve) => setTimeout(resolve, 100))
    const currentState = useSessionStateStore.getState()
    
    // Verify consent was set correctly
    if (currentState.userConsent?.photoPublication !== consent) {
      console.warn('[CONSENT] Consent value mismatch, retrying state update')
      setSessionState({
        userConsent: {
          photoPublication: consent,
          consentTimestamp: new Date()
        }
      })
      await new Promise((resolve) => setTimeout(resolve, 100))
    }
    
    // Upload session state with consent
    await uploadSessionState(id)
    console.log('[CONSENT] Uploaded consent:', consent)
  }

  // Initial setup effect
  useEffect(() => {
    setShowConsent(true)

    // Set up countdown callbacks
    resultCountdown.setOnTick(() => {
      setRemainingTime(resultCountdown.getFormattedRemainingSeconds())
    })

    resultCountdown.setOnComplete(() => {
      // Log countdown timeout for editing
      logActivity(
        `Edit result page timed out after ${config?.settings.resultTimeout || 120} seconds.`,
        {
          mode: 'edit'
        }
      )
      // For edit mode, go back to root and cleanup
      sessionCleanup()
      window.location.href = '/'
    })

    const setupResult = async (): Promise<void> => {
      if (hasCompleted.current) return
      hasCompleted.current = true

      let finalStripPreview = ''

      if (gallery.capturedItems && gallery.capturedItems.length > 0) {
        // Find the edited strip URL
        const stripItem = gallery.capturedItems.find((item) =>
          item.id?.includes('dreambooth-edit-strip')
        )
        finalStripPreview = stripItem?.stripUrl || ''
        setStripPreview(finalStripPreview)
      }

      logActivity(`Edit session completed!`, {
        mode: 'edit',
        stripUrl: finalStripPreview,
        originalSessionId: gallery.originalSessionId
      })

      // FIXED: Only update timeline completion, preserve other session data
      setSessionState({
        timeline: {
          isCompleted: true
        }
      })

      // Generate QR code for the original gallery (not the edit session)
      const originalGalleryId = gallery.originalSessionId || id
      const galleryTarget = resolveGallerySessionUrl(originalGalleryId, config?.settings)
      QRCode.toDataURL(galleryTarget, {
        margin: 0,
        width: 300
      }).then(setQrCodeImage)

      const completeSession = async (): Promise<void> => {
        // Wait for state to propagate
        await new Promise((resolve) => setTimeout(resolve, 100))
        // Edit mode always requires consent; handleConsent handles the upload. Do not upload here.
        console.log('[RESULT] Skipping initial upload - waiting for user consent')
      }

      completeSession()
    }

    setupResult()

    return (): void => {
      resultCountdown.stop()
    }
  }, [])

  return (
    <>
      {/* Consent Modal */}
      <ConsentModal
        isVisible={showConsent}
        onConsent={handleConsent}
        stripPreview={stripPreview}
        mode="capture"
      />

      {/* Main Result Content - Only show after consent */}
      {!showConsent && consentGiven !== null && (
        <div className="w-full h-full bg-whiteout flex flex-col items-center justify-center relative">
          {/* Main Content */}
          <div
            className={`flex flex-col items-center ${isPotrait ? 'w-full px-8' : 'w-[90%] px-12'} gap-12`}
          >
            {/* Title */}
            <div className="text-center mb-4">
              <h1 className={`${isPotrait ? 'text-5xl' : 'text-6xl'} text-carbon font-bold mb-6`}>
                Your Edited Strip Is Ready!
              </h1>
              <p className={`${isPotrait ? 'text-2xl' : 'text-3xl'} text-carbon/80`}>
                Scan the QR code below to access your edited photos
              </p>
            </div>

            {/* Preview and QR Section */}
            <div className={`flex flex-row items-center gap-16`}>
              {/* Preview */}
              <div className="relative">
                {stripPreview ? (
                  <img
                    className={`${
                      isPotrait ? 'h-[50vh] max-h-[600px]' : 'h-[65vh] max-h-[800px]'
                    } w-auto object-contain drop-shadow-2xl rounded-lg`}
                    src={stripPreview || ''}
                    alt="Edited Photo Strip"
                  />
                ) : null}
              </div>

              {/* QR Code Section */}
              <div className={`flex flex-col items-center gap-8 ${isPotrait ? 'mt-8' : ''}`}>
                <div className="bg-white p-8 rounded-3xl shadow-2xl ring-1 ring-carbon/10 flex items-center justify-center">
                  {qrCodeImage ? (
                    <img
                      className={`${isPotrait ? 'w-[180px] h-[180px]' : 'w-[250px] h-[250px]'}`}
                      src={qrCodeImage}
                      alt="QR Code"
                    />
                  ) : (
                    <div
                      className={`${isPotrait ? 'w-[180px] h-[180px]' : 'w-[250px] h-[250px]'} flex items-center justify-center`}
                    >
                      <svg
                        className={`${isPotrait ? 'w-16 h-16' : 'w-20 h-20'} animate-spin text-carbon/40`}
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                    </div>
                  )}
                </div>
                <div className="text-center">
                  <p className={`text-carbon/80 ${isPotrait ? 'text-xl' : 'text-2xl'} mb-2`}>
                    Original Gallery ID:
                  </p>
                  <p className={`text-carbon ${isPotrait ? 'text-2xl' : 'text-3xl'} font-mono`}>
                    {gallery.originalSessionId}
                  </p>
                  <p className={`text-carbon/60 ${isPotrait ? 'text-sm' : 'text-base'} mt-1`}>
                    (Your edited strip has been added to this gallery)
                  </p>
                </div>
                <div
                  className={`flex items-center gap-3 text-carbon/80 ${isPotrait ? 'text-lg' : 'text-xl'}`}
                >
                  <FaQrcode size={isPotrait ? 20 : 24} />
                  <span>Scan to view gallery</span>
                </div>
                <div
                  className={`flex items-center gap-2 text-carbon/60 ${isPotrait ? 'text-base' : 'text-lg'}`}
                >
                  <FaImage />
                  <span>Edited Photo Strip</span>
                </div>
              </div>
            </div>
          </div>

          {/* Bottom Actions */}
          <div className="absolute bottom-24 right-12 z-30">
            {(config?.settings?.resultTimeout || 0) > 0 && (
              <span
                className={`block text-center text-carbon/80 ${isPotrait ? 'text-xl' : 'text-2xl'} mb-4`}
              >
                {remainingTime}
              </span>
            )}
            <Button
              text="Back to Edit"
              onClick={() => navigate(`/${projectId}`)}
              data-nav-to="welcome"
              variant="primary"
              className={`${isPotrait ? '!text-lg !py-3 !px-6' : 'text-xl !py-4 !px-8'}`}
            />
          </div>
        </div>
      )}
    </>
  )
}

export default EditResultPage
